import type { ReactNode } from "react";
import { styled } from "tamagui";
import { View, XStack, YStack } from "../Stacks";
const ROW_CONTENT_GAP = "$sp0.5" as const;
const ROW_LINE_GAP = "$sp1.5" as const;

// flexGrow (basis auto) instead of `flex: 1` (basis 0) so hug-content rows
// keep their natural width — `flex: 1` collapses them to 0 on native.
const RowContent = styled(YStack, {
  name: "RowContent",
  flexGrow: 1,
  flexShrink: 1,
  gap: ROW_CONTENT_GAP
});
type RowLayoutProps = {
  /** Vertical alignment of leading element with the content column. */
  alignItems?: "center" | "flex-start";
  /** Left-hand slot — typically an Avatar, Icon, or custom leading element. */
  leading?: ReactNode;
  /** Primary content (title/label). Caller supplies typography. */
  primary: ReactNode;
  /** Inline trailing element on the primary row (badge, pip, selector, data). */
  primaryTrailing?: ReactNode;
  /** Secondary content (subtitle/description). Caller supplies typography. */
  secondary?: ReactNode;
  /** Inline trailing element on the secondary row (metadata, etc.). */
  secondaryTrailing?: ReactNode;
  /** Outer trailing slot — outside the content column (chevron, action button). */
  rowTrailing?: ReactNode;
};

/**
 * Internal layout shared by ListItem, SelectionRow, and InfoRow.
 * Typography and outer padding are caller concerns.
 */
export const RowLayout = ({
  alignItems = "center",
  leading,
  primary,
  primaryTrailing,
  secondary,
  secondaryTrailing,
  rowTrailing
}: RowLayoutProps) =>
// No flexGrow on the outer XStack — width fill comes from the parent's
// default `alignItems: stretch`. flexGrow here would grow the row along
// the parent's main axis (vertical when parent is a YStack like
// SelectionCard / Card / InfoRow's container), stretching the row to
// any available height. ListItem's XStack parent achieves the same width
// fill via being a YStack (since the RowLayout is its only child).
<XStack alignItems={alignItems} gap={ROW_LINE_GAP} data-sentry-element="XStack" data-sentry-component="RowLayout" data-sentry-source-file="RowLayout.tsx">
    {leading != null ? <View flexShrink={0}>{leading}</View> : null}
    <RowContent data-sentry-element="RowContent" data-sentry-source-file="RowLayout.tsx">
      <XStack alignItems="center" justifyContent="space-between" gap={ROW_LINE_GAP} data-sentry-element="XStack" data-sentry-source-file="RowLayout.tsx">
        {primary}
        {primaryTrailing != null ? <View flexShrink={0}>{primaryTrailing}</View> : null}
      </XStack>
      {secondary != null || secondaryTrailing != null ? <XStack alignItems="flex-end" gap={ROW_LINE_GAP}>
          <XStack flex={1} minWidth={0}>
            {secondary}
          </XStack>
          {secondaryTrailing != null ? <XStack marginLeft="auto" flexShrink={0}>
              {secondaryTrailing}
            </XStack> : null}
        </XStack> : null}
    </RowContent>
    {rowTrailing != null ? <View flexShrink={0}>{rowTrailing}</View> : null}
  </XStack>;
export type { RowLayoutProps };